import { motion } from 'framer-motion';
import {
  Users,
  MessageSquare,
  Share2,
  UserPlus,
  CheckCircle,
  ArrowRight,
  Target,
  Bell,
  FileText,
  Calendar,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function TeamCollaborationPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const collaborationFeatures = [
    {
      icon: UserPlus,
      title: 'Team Invitations',
      description:
        'Invite your leadership team, department heads, and key stakeholders to collaborate on your strategic plan.',
      benefits: ['Role-based permissions', 'Email invitations', 'Team access with Pro Activation'],
    },
    {
      icon: MessageSquare,
      title: 'Organization Structure',
      description:
        'Connect people to divisions, teams and positions so the plan reflects how your company works.',
      benefits: ['Visual organization chart', 'Teams and divisions', 'Position assignments'],
    },
    {
      icon: FileText,
      title: 'Shared Strategic Context',
      description:
        'Keep your company direction, assessments and chosen priorities in a workspace your team can refer to.',
      benefits: ['Company direction', 'Shared assessments', 'Connected priorities'],
    },
    {
      icon: Target,
      title: 'Task Assignment',
      description:
        'Assign strategic projects and tasks to specific team members with clear ownership.',
      benefits: ['Clear accountability', 'Progress tracking', 'Deadline management'],
    },
    {
      icon: Bell,
      title: 'Connected Projects',
      description:
        'Connect strategic priorities to projects and tasks so people can see the purpose behind the work.',
      benefits: ['Strategic projects', 'Tasks and subtasks', 'Progress views'],
    },
    {
      icon: Share2,
      title: 'Stakeholder Sharing',
      description:
        'Bring your strategic plan into a visual report for reviews, leadership conversations and stakeholder updates.',
      benefits: ['Visual strategic reports', 'Company context', 'PDF exports'],
    },
  ];

  const useCases = [
    {
      title: 'Executive Leadership Team',
      scenario: 'CEO, CFO, COO, and VPs need to collaboratively develop company strategy',
      solution:
        'Each executive contributes their domain expertise while maintaining visibility into the complete picture',
      outcome: 'Comprehensive strategy reflecting all functional perspectives',
    },
    {
      title: 'Distributed Teams',
      scenario: 'Team members across multiple locations and time zones',
      solution:
        'A shared company direction with visible teams, roles and responsibility for strategic work',
      outcome: 'A common plan people can refer to across locations',
    },
    {
      title: 'Board-Management Alignment',
      scenario: 'Board wants visibility into strategy without micromanaging execution',
      solution:
        'Management develops strategy; Board receives curated updates and milestone reports',
      outcome: 'Transparent governance with appropriate engagement levels',
    },
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Shared context',
      description:
        'Keep company direction and strategic priorities together, so your team can see how their work fits.',
    },
    {
      icon: Users,
      title: 'A role in the plan',
      description:
        'Bring your people into the planning process and make their responsibilities visible.',
    },
    {
      icon: Target,
      title: 'Less searching for context',
      description:
        'Keep the plan and its ownership together when you review decisions and next steps.',
    },
    {
      icon: MessageSquare,
      title: 'Clear accountability',
      description:
        'Connect strategic work to the people, teams and roles responsible for delivery.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'Team Collaboration' }]} />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        title="One strategy. A team that knows its part."
        subtitle="With Pro Activation, bring your company direction, people and responsibilities into one connected plan. Give everyone the context to understand the priorities and own the next step."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Connect my team and strategy
        </Link>
      </PageHero>

      {/* Collaboration Features */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">
              Make the plan easy to understand and act on.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything your team needs to work together effectively on strategic planning
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {collaborationFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="bg-blue-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Bring your people into the plan.</h2>
            <p className="text-xl text-gray-600">
              Simple workflow for distributed strategy development
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            {[
              {
                step: '1',
                title: 'Invite Your Team',
                description:
                  'Invite team members by email and give them the appropriate company role.',
                icon: UserPlus,
              },
              {
                step: '2',
                title: 'Assign Responsibilities',
                description:
                  'Designate who owns each strategic element (e.g., CFO leads financial objectives)',
                icon: Target,
              },
              {
                step: '3',
                title: 'Work from Shared Context',
                description:
                  'Bring different perspectives to a shared plan, then refine the direction and priorities together.',
                icon: MessageSquare,
              },
              {
                step: '4',
                title: 'Track & Execute Together',
                description: 'Monitor progress, update status, and ensure accountability as a team',
                icon: Calendar,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-lg flex items-center justify-center font-bold text-xl">
                  {item.step}
                </div>
                <div>
                  <div className="flex items-center mb-2">
                    <item.icon className="w-5 h-5 text-blue-600 mr-2" />
                    <h3 className="text-xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              A shared direction for the way your team works.
            </h2>
            <p className="text-xl text-gray-600">
              See how different organizations collaborate on strategy
            </p>
          </motion.div>

          <div className="space-y-8 max-w-5xl mx-auto">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-xl border border-blue-200"
              >
                <h3 className="text-2xl font-bold mb-4 text-blue-600">{useCase.title}</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Scenario</h4>
                    <p className="text-gray-600 text-sm">{useCase.scenario}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">How They Use It</h4>
                    <p className="text-gray-600 text-sm">{useCase.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Outcome</h4>
                    <p className="text-green-600 font-semibold text-sm">{useCase.outcome}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Turn alignment into clear ownership.</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card text-center bg-white p-6 rounded-xl shadow-sm"
              >
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Keep the plan alongside the people delivering it.
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="public-card bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4 text-red-600">Traditional Collaboration</h3>
                <ul className="space-y-3 text-gray-600">
                  <li>❌ Endless email threads</li>
                  <li>❌ Version confusion (StratPlan_v3_final_FINAL.docx)</li>
                  <li>❌ Long meetings with unclear outcomes</li>
                  <li>❌ Decisions lost in Slack/Teams history</li>
                  <li>❌ Hard to track who's responsible for what</li>
                  <li>❌ No visibility into overall progress</li>
                </ul>
              </div>

              <div className="public-card bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-xl font-bold mb-4 text-blue-600">StratPlan Collaboration</h3>
                <ul className="space-y-3 text-gray-700">
                  <li>✅ Contextual discussions in one place</li>
                  <li>✅ Single source of truth, always current</li>
                  <li>✅ Async collaboration, fewer meetings</li>
                  <li>✅ All decisions documented and searchable</li>
                  <li>✅ Clear ownership and accountability</li>
                  <li>✅ Projects and tasks connected to the strategy</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="public-section public-cta py-16 bg-blue-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={false} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Give your team a direction they can own.
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Bring your company direction, people and priorities together. Start with a clear plan,
              then give your team a part in delivering it.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Build my strategic plan <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
